import { apiUrls } from '../ApiUrls';
import {
  Internship
} from '../types/internship.types';
import {
  InternshipListRequest,
  InternshipSearchFilters,
} from '../types/internship.list.request';
import {
  InternshipListResponse,
} from '../types/internship.list.response';
import { BaseApiService } from '../BaseApiService';
import { ApiResponse } from '../ApiResponse';
import { InternshipEligibility, InternshipSkill } from '../types/create-internsship.request.types';

/**
 * Service class for internship-related API operations
 * Connects to the backend InternshipController endpoints
 */
class InternshipService extends BaseApiService {
  private static instance: InternshipService;

  public static getInstance(): InternshipService {
    if (!InternshipService.instance) {
      InternshipService.instance = new InternshipService();
    }
    return InternshipService.instance;
  }

  /**
   * Get paginated list of internships (public endpoint)
   */
  static async getInternships(request: InternshipListRequest): Promise<ApiResponse<InternshipListResponse>> {
    console.log('🔍 [InternshipService] Fetching internships with request:', request);
    const response = await this.post(apiUrls.internships.list, request, 'Get Internships');
    return response;
  }

  /**
   * Get internships with simplified filters
   */
  static async getInternshipsWithFilters(filters: InternshipSearchFilters): Promise<ApiResponse<InternshipListResponse>> {
    console.log('🔍 [InternshipService] Fetching internships with filters:', filters);
    const request: InternshipListRequest = {
      page: 1,
      limit: 10,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      searchFilters: Object.keys(filters).length > 0 ? filters : undefined,
    };
    return this.getInternships(request);
  }

  static async getInternshipsByOrgId(request: InternshipListRequest): Promise<ApiResponse<InternshipListResponse>> {
    console.log('🔍 [InternshipService] Fetching internships by org id:', request);
    return await this.postAuthenticated(apiUrls.internships.byLoginOrgId, request, 'Get Internships by Org Id');
  }

  /**
   * Create a new internship (protected endpoint)
   */
  static async createInternship(internshipData: Internship): Promise<any> {
    try {
      console.log('➕ [InternshipService] Creating new internship:', internshipData.internshipInfo?.title || 'Untitled');
      return await this.postAuthenticated(apiUrls.internships.create, internshipData, 'Create Internship');
    } catch (error: any) {
      console.error('❌ [InternshipService] Error creating internship:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to create internship',
        errorCode: error.errorCode,
      };
    }
  }


  /**
  * Add internship eligibility (protected endpoint)
  */
  static async addInternshipEligibility(internshipCodeOrId: string | number, eligibilityData: InternshipEligibility): Promise<any> {
    try {
      console.log('➕ [InternshipService] Adding internship eligibility:', eligibilityData.courseCode || 'Untitled');
      return await this.postAuthenticated(
        apiUrls.internships.addEligibility(internshipCodeOrId),
        eligibilityData,
        'Add Internship Eligibility'
      );
    } catch (error: any) {
      console.error('❌ [InternshipService] Error adding internship eligibility:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to add internship eligibility',
      };
    }
  }

  static async getInternshipEligibilityList(internshipCodeOrId: string | number): Promise<any> {
    try {
      console.log('🔍 [InternshipService] Fetching internship eligibility:', internshipCodeOrId);
      return await this.getAuthenticated(
        apiUrls.internships.eligibilityList(internshipCodeOrId),
        'Get Internship Eligibility List'
      );
    } catch (error: any) {
      console.error('❌ [InternshipService] Error fetching internship eligibility:', error);

      return {
        status: 'error',
        message: error.message || 'Failed to fetch internship eligibility',
        data: null,
      };
    }
  }

  static async deleteInternshipEligibility(eligibilityId: number): Promise<any> {
    try {
      console.log('🔍 [InternshipService] Deleting internship eligibility:', eligibilityId);
      return await this.deleteAuthenticated(apiUrls.internships.deleteEligibility(eligibilityId), 'Delete Internship Eligibility');
    } catch (error: any) {
      console.error('❌ [InternshipService] Error deleting internship eligibility:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to delete internship eligibility',
        data: null,
      };
    }
  }

  static async addInternshipSkill(internshipCodeOrId: string | number, skillData: InternshipSkill): Promise<any> {
    try {
      console.log('➕ [InternshipService] Adding internship skill:', skillData.skillCode || 'Untitled');
      return await this.postAuthenticated(
        apiUrls.internships.addSkill(internshipCodeOrId),
        skillData,
        'Add Internship Skill'
      );
    } catch (error: any) {
      console.error('❌ [InternshipService] Error adding internship skill:', error);

      return {
        status: 'error',
        message: error.message || 'Failed to add internship skill',
        data: null,
      };
    }
  }

  static async getInternshipSkillList(internshipCodeOrId: string | number): Promise<ApiResponse<InternshipSkill[]>> {
    try {
      console.log('🔍 [InternshipService] Fetching internship skill:', internshipCodeOrId);
      return await this.getAuthenticated(
        apiUrls.internships.skillList(internshipCodeOrId),
        'Get Internship Skill List'
      );
    } catch (error: any) {
      console.error('❌ [InternshipService] Error fetching internship skill:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to fetch internship skill',
        data: [],
      };
    }
  }

  static async deleteInternshipSkill(internshipSkillId: number): Promise<any> {
    try {
      console.log('🗑️ [InternshipService] Deleting internship skill:', internshipSkillId);
      return await this.deleteAuthenticated(apiUrls.internships.deleteSkill(internshipSkillId), 'Delete Internship Skill');
    } catch (error: any) {
      console.error('❌ [InternshipService] Error deleting internship skill:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to delete internship skill',
      };
    }
  }

  /**
   * Add multiple internship documents (protected endpoint)
   */
  static async addInternshipDocuments(
    internshipCodeOrId: string | number,
    payload: { internshipId: number; documents: any[] }
  ): Promise<any> {
    try {
      console.log('➕ [InternshipService] Adding multiple internship documents');
      return await this.postAuthenticated(
        apiUrls.internships.addMultipleDocuments(internshipCodeOrId),
        payload,
        'Add Multiple Internship Documents'
      );
    } catch (error: any) {
      console.error('❌ [InternshipService] Error adding multiple internship documents:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to add multiple internship documents',
      };
    }
  }

  static async getInternshipDocumentList(internshipCodeOrId: string | number): Promise<any> {
    try {
      console.log('🔍 [InternshipService] Fetching internship documents:', internshipCodeOrId);
      return await this.getAuthenticated(
        apiUrls.internships.documentList(internshipCodeOrId),
        'Get Internship Document List'
      );
    } catch (error: any) {
      console.error('❌ [InternshipService] Error fetching internship documents:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to fetch internship documents',
      };
    }
  }

  static async deleteInternshipDocument(internshipDocumentId: number): Promise<any> {
    try {
      console.log('🗑️ [InternshipService] Deleting internship document:', internshipDocumentId);
      return await this.deleteAuthenticated(apiUrls.internships.deleteDocument(internshipDocumentId), 'Delete Internship Document');
    } catch (error: any) {
      console.error('❌ [InternshipService] Error deleting internship document:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to delete internship document',
      };
    }
  }

  /**
   * Update an existing internship (protected endpoint)
   */
  static async updateInternship(internshipCodeOrId: string | number, internshipData: Internship): Promise<any> {
    try {
      console.log('✏️ [InternshipService] Updating internship:', internshipCodeOrId);
      const response = await this.putAuthenticated(
        apiUrls.internships.update(internshipCodeOrId),
        internshipData,
        'Update Internship'
      );
      if (response.status === 'success') {
        return {
          status: 'success',
          data: response.data,
          message: 'Internship updated successfully',
        };
      } else {
        throw new Error(response.message || 'Failed to update internship');
      }
    } catch (error: any) {
      console.error('❌ [InternshipService] Error updating internship:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to update internship',
        data: null,
      };
    }
  }

  static async submitInternship(internshipCodeOrId: string | number): Promise<any> {
    try {
      console.log('✏️ [InternshipService] Submitting internship:', internshipCodeOrId);
      return await this.putAuthenticated(
        apiUrls.internships.submit(internshipCodeOrId),
        {},
        'Submit Internship'
      );
    } catch (error: any) {
      console.error('❌ [InternshipService] Error submitting internship:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to submit internship',
        data: null,
      };
    }
  }

  /**
   * Get internship by ID (public endpoint)
   */
  static async getInternshipById(id: number): Promise<any> {
    try {
      console.log('🔍 [InternshipService] Fetching internship by ID:', id);
      const response = await this.getAuthenticated(apiUrls.internships.details(id), 'Get Internship by ID');
      if (response.status === 'success') {
        return {
          status: 'success',
          data: response.data,
          message: 'Internship fetched successfully',
        };
      } else {
        throw new Error(response.message || 'Failed to fetch internship');
      }
    } catch (error: any) {
      console.error('❌ [InternshipService] Error fetching internship:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to fetch internship',
        data: null,
      };
    }
  }

  /**
   * Get internship by code (public endpoint)
   */
  static async getInternshipByCode(internshipCode: string): Promise<any> {
    try {
      console.log('🔍 [InternshipService] Fetching internship by code:', internshipCode);
      const response = await this.get(apiUrls.internships.detailsByCode(internshipCode), 'Get Internship by Code');
      if (response.status === 'success') {
        return {
          status: 'success',
          data: response.data,
          message: 'Internship fetched successfully',
        };
      } else {
        throw new Error(response.message || 'Failed to fetch internship');
      }
    } catch (error: any) {
      console.error('❌ [InternshipService] Error fetching internship by code:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to fetch internship',
        data: null,
      };
    }
  }

  /**
   * Get internship by code (authenticated)
   */
  static async getInternshipByCodeAuthenticated(internshipCode: string): Promise<any> {
    try {
      console.log('🔍 [InternshipService] Fetching internship by code (auth):', internshipCode);
      const response = await this.getAuthenticated(
        apiUrls.internships.detailsByCode(internshipCode),
        'Get Internship by Code'
      );
      if (response.status === 'success') {
        return {
          status: 'success',
          data: response.data,
          message: 'Internship fetched successfully',
        };
      } else {
        throw new Error(response.message || 'Failed to fetch internship');
      }
    } catch (error: any) {
      console.error('❌ [InternshipService] Error fetching internship by code (auth):', error);
      return {
        status: 'error',
        message: error.message || 'Failed to fetch internship',
        data: null,
      };
    }
  }

  /**
   * Delete an internship (protected endpoint)
   */
  static async deleteInternship(internshipCodeOrId: string | number): Promise<any> {
    try {
      console.log('🗑️ [InternshipService] Deleting internship:', internshipCodeOrId);
      const response = await this.deleteAuthenticated(
        apiUrls.internships.delete(internshipCodeOrId),
        'Delete Internship'
      );
      if (response.status === 'success') {
        return {
          status: 'success',
          data: response.data,
          message: 'Internship deleted successfully',
        };
      } else {
        throw new Error(response.message || 'Failed to delete internship');
      }
    } catch (error: any) {
      console.error('❌ [InternshipService] Error deleting internship:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to delete internship',
        data: null,
      };
    }
  }

  /**
   * Apply for an internship (protected endpoint)
   */
  static async applyForInternship(internshipId: number, supportingDocs: string[], applicationNotes?: string): Promise<any> {
    try {
      console.log('📝 [InternshipService] Applying for internship:', internshipId);

      // Prepare the request payload matching SubmitInternshipApplicationRequest
      const requestPayload = {
        uploadedDocuments: supportingDocs,
        applicationNotes: applicationNotes || '' // Use provided notes or empty string
      };

      const response = await this.postAuthenticated(apiUrls.internships.applySubmit(internshipId), requestPayload, 'Apply for Internship');
      if (response.status === 'success') {
        return {
          status: 'success',
          data: response.data,
          message: 'Application submitted successfully',
        };
      } else {
        // Return the actual error message from the API response
        return {
          status: 'error',
          message: response.message || 'Failed to submit application',
          data: null,
        };
      }
    } catch (error: any) {
      console.error('❌ [InternshipService] Error applying for internship:', error);
      // Extract error message from the error response if available
      let errorMessage = 'Failed to submit application';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      return {
        status: 'error',
        message: errorMessage,
        data: null,
      };
    }
  }

  /**
   * Apply for an internship by code (protected endpoint)
   */
  static async applyForInternshipByCode(internshipCode: string, supportingDocs: string[], applicationNotes?: string): Promise<any> {
    try {
      console.log('📝 [InternshipService] Applying for internship code:', internshipCode);

      const requestPayload = {
        uploadedDocuments: supportingDocs,
        applicationNotes: applicationNotes || ''
      };

      const response = await this.postAuthenticated(
        apiUrls.internships.applySubmitByCode(internshipCode),
        requestPayload,
        'Apply for Internship'
      );
      if (response.status === 'success') {
        return {
          status: 'success',
          data: response.data,
          message: 'Application submitted successfully',
        };
      } else {
        return {
          status: 'error',
          message: response.message || 'Failed to submit application',
          data: null,
        };
      }
    } catch (error: any) {
      console.error('❌ [InternshipService] Error applying for internship by code:', error);
      let errorMessage = 'Failed to submit application';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      return {
        status: 'error',
        message: errorMessage,
        data: null,
      };
    }
  }

  /**
   * Get internship statistics (public endpoint)
   */
  static async getInternshipStats(): Promise<any> {
    try {
      console.log('📊 [InternshipService] Fetching internship statistics...');
      const response = await this.get(apiUrls.internships.stats, 'Get Internship Statistics');
      if (response.status === 'success') {
        return {
          status: 'success',
          data: response.data,
          message: 'Statistics fetched successfully',
        };
      } else {
        throw new Error(response.message || 'Failed to fetch statistics');
      }
    } catch (error: any) {
      console.error('❌ [InternshipService] Error fetching statistics:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to fetch statistics',
        data: null,
      };
    }
  }

  /**
   * Update internship status (protected endpoint)
   */
  static async updateInternshipStatus(id: number | string, status: string): Promise<any> {
    try {
      console.log('✏️ [InternshipService] Updating internship status:', id, status);
      const payload = { id: id.toString(), status };
      return await this.postAuthenticated(apiUrls.internships.updateStatus, payload, 'Update Internship Status');
    } catch (error: any) {
      console.error('❌ [InternshipService] Error updating internship status:', error);
      return {
        status: 'error',
        message: error.message || 'Failed to update internship status',
        data: null,
      };
    }
  }
}

// Export the class for static method access
export { InternshipService }; 